import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Store, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MapPin, AlertTriangle, ArrowUpDown, DollarSign, Filter, CheckCircle2, Info } from 'lucide-react'
import { useStoreList } from '../hooks/useStore.js'
import { trackEvent } from '../analytics/mixpanel.js'
import SignalBadge from '../components/shared/SignalBadge.jsx'
import SeverityBar from '../components/shared/SeverityBar.jsx'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import Tooltip from '../components/shared/Tooltip.jsx'
import StatStrip from '../components/shared/StatStrip.jsx'
import CollapsibleFilterBar from '../components/shared/CollapsibleFilterBar.jsx'
import FilterField from '../components/shared/FilterField.jsx'
import { DC_CODES, dcLabel } from '../utils/dc.js'
import { clsx } from 'clsx'

const DC_LABELS = {
  HZL: 'Hazleton, PA — Distribution Center #002959',
  MCI: 'Ottawa, KS — Distribution Center #000659',
  CDC: 'Mississauga, ON — Distribution Center #000978',
  LA_3PL: 'Los Angeles, CA — 3rd-party logistics hub',
}

const SEVERITY_TIPS = {
  0: 'Clear — no significant weather forecast. Normal operations expected.',
  1: 'Minor — light weather event. Negligible impact on traffic or deliveries.',
  2: 'Moderate — noticeable weather. Some foot-traffic reduction likely.',
  3: 'Significant — moderate storm. Meaningful traffic drop and possible delivery delays.',
  4: 'Severe — major weather event. Significant disruption to store traffic and supply lanes.',
  5: 'Extreme — catastrophic conditions. Store closure and full supply chain disruption possible.',
}

function wosTip(wos) {
  if (wos < 2) return `${wos.toFixed(1)} weeks of supply — Critical. Near stock-out risk; urgent replenishment needed.`
  if (wos < 4) return `${wos.toFixed(1)} weeks of supply — Low. High risk of stock-out if a delivery is delayed.`
  if (wos < 8) return `${wos.toFixed(1)} weeks of supply — Moderate. Monitor closely and plan replenishment soon.`
  return `${wos.toFixed(1)} weeks of supply — Healthy. Sufficient inventory for normal supply disruptions.`
}

const PAGE_SIZE = 48

function fmtVar(v) {
  if (!v || v <= 0) return null
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v}`
}

function StoreCard({ store, onClick, showVar }) {
  const signal = store.forecast_severity >= 4 ? 'STOP' : 'GO'
  const varLabel = fmtVar(store.value_at_risk)

  return (
    <motion.div
      className="glass-card p-4 cursor-pointer hover:border-cyan-500/30 transition-all duration-200 group"
      onClick={onClick}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="font-mono font-black text-cyan-400 text-lg group-hover:text-glow-accent transition-all">
          #{store.store_id}
        </span>
        <Tooltip
          content={signal === 'STOP'
            ? 'STOP — Severe weather (severity 4+) forecast. Hold replenishment and review allocations.'
            : 'GO — No severe weather forecast. Normal replenishment and allocation operations.'}
          placement="top"
        >
          <SignalBadge signal={signal} size="sm" />
        </Tooltip>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
        <MapPin size={10} />
        <span>
          {store.city !== 'Unknown' ? `${store.city}, ${store.state}` : 'Location unknown'}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs">
        <Tooltip content={DC_LABELS[store.primary_dc] ?? store.primary_dc} placement="bottom">
          <span className="px-2 py-0.5 rounded bg-navy-600/60 text-slate-400 border border-white/8 font-bold cursor-help">
            {store.primary_dc}
          </span>
        </Tooltip>
        <div className="flex items-center gap-1.5">
          <Tooltip content="Weeks of Supply — how many weeks of inventory the store has on hand based on average weekly sales velocity" placement="top">
            <span className="text-slate-600 cursor-help border-b border-dashed border-slate-700">WOS:</span>
          </Tooltip>
          <Tooltip content={wosTip(store.current_wos ?? 0)} placement="top">
            <span className={clsx(
              'font-semibold cursor-help',
              store.current_wos < 4 ? 'text-red-400' :
              store.current_wos < 8 ? 'text-amber-400' :
              'text-emerald-400'
            )}>
              {(store.current_wos ?? 0).toFixed(1)}w
            </span>
          </Tooltip>
        </div>
      </div>

      {store.forecast_severity > 0 && (
        <Tooltip
          content={`Max forecast severity: ${SEVERITY_TIPS[Math.min(5, store.forecast_severity)] ?? ''}`}
          placement="bottom"
        >
          <div className="mt-3 pt-3 border-t border-white/5 cursor-help">
            <SeverityBar severity={store.forecast_severity} showLabel={false} />
          </div>
        </Tooltip>
      )}

      {varLabel && (
        <Tooltip content={`$${store.value_at_risk.toLocaleString()} estimated P&L impact from active prescriptive actions for this store`} placement="bottom">
          <div className="mt-2 flex items-center gap-1 cursor-help">
            <DollarSign size={9} className="text-amber-400 flex-shrink-0" />
            <span className="text-[10px] font-bold text-amber-400">{varLabel} at risk</span>
          </div>
        </Tooltip>
      )}
    </motion.div>
  )
}

export default function StoreList() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [dc, setDc] = useState('')
  const [signal, setSignal] = useState('')
  const [sortBy, setSortBy] = useState('store_id')

  const { data, isLoading, isError, isFetching, refetch } = useStoreList({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    dc,
    signal,
    sortBy,
  })

  const handleSearch = (val) => {
    setSearch(val)
    clearTimeout(window.__storeSearchTimer)
    window.__storeSearchTimer = setTimeout(() => {
      setDebouncedSearch(val)
      setPage(1)
    }, 350)
  }

  const handleDcFilter = (val) => {
    setDc(val)
    setPage(1)
  }

  const totalPages = data?.total_pages ?? 1

  return (
    <div className="min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)] flex flex-col">
      {/* ── Fixed header area ── */}
      <div className="flex-shrink-0 max-w-7xl w-full mx-auto px-4 sm:px-6 pt-8">
        {/* Header */}
        <motion.div
          className="mb-6 flex items-baseline gap-x-3 gap-y-1 flex-wrap"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-black text-slate-100 flex items-center gap-2">
            <Store size={20} className="text-cyan-400" />
            Store Directory
            <Tooltip content="All monitored stores with their weather exposure and inventory sensitivity — search or click a row to open store detail" placement="bottom" asButton>
              <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
            </Tooltip>
          </h1>
          <p className="text-slate-400 text-sm">
            {data ? `${data.total.toLocaleString()} stores monitored` : 'Loading...'}
            {isFetching && !isLoading && ' · Refreshing...'}
          </p>
        </motion.div>

        {/* Summary Stats */}
        {data && (
          <StatStrip
            items={[
              { label: 'Total stores', value: data.total.toLocaleString(), icon: Filter, tone: 'slate' },
              { label: 'STOP signals', value: (data.stop_count ?? 0).toLocaleString(), icon: AlertTriangle, tone: 'danger' },
              { label: 'GO signals', value: (data.go_count ?? 0).toLocaleString(), icon: CheckCircle2, tone: 'success' },
            ]}
          />
        )}

        {/* Filter Bar */}
        <CollapsibleFilterBar
          persistKey="aeo:filters:open:stores"
          activeSummary={[
            dc && `DC: ${dc}`,
            signal && `Signal: ${signal}`,
            debouncedSearch && `Search: "${debouncedSearch}"`,
            sortBy === 'value_at_risk' && 'Sort: Value at Risk',
          ].filter(Boolean).join(' · ') || 'All stores'}
        >
          <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
          <FilterField label="Search">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search by store ID or city..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
              />
            </div>
          </FilterField>

          <FilterField label="Distribution center">
            <select
              value={dc}
              onChange={(e) => handleDcFilter(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              <option value="">All DCs</option>
              {DC_CODES.map((d) => (
                <option key={d} value={d}>{dcLabel(d)}</option>
              ))}
            </select>
          </FilterField>

          {/* Signal filter */}
          <FilterField label="Signal">
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {[
                { value: '', label: 'All' },
                { value: 'STOP', label: '🔴 STOP' },
                { value: 'GO', label: '🟢 GO' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => { setSignal(value); setPage(1) }}
                  className={`px-3 py-2 text-xs font-semibold transition-colors ${
                    signal === value
                      ? value === 'STOP'
                        ? 'bg-red-500/20 text-red-300'
                        : value === 'GO'
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </FilterField>

          </div>

          {/* Display options — ordering, separated from the data filters above */}
          <div className="border-t border-white/5 pt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Sort by</span>
            <ArrowUpDown size={13} className="text-slate-500 flex-shrink-0" />
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {[
                { value: 'store_id', label: 'Store ID' },
                { value: 'value_at_risk', label: 'Value at Risk' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => { setSortBy(value); setPage(1) }}
                  className={`px-3 py-2 text-xs font-semibold transition-colors ${
                    sortBy === value
                      ? value === 'value_at_risk'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          </div>
        </CollapsibleFilterBar>
      </div>

      {/* ── Scrollable grid ──
          Desktop (xl+, 1280+): fills remaining viewport, internal scroll.
          Below xl: bounded height (500–700px) with internal scroll;
          the page itself scrolls if the zoomed-in content overflows. */}
      <div className="overflow-y-auto min-h-[300px] max-h-[min(70vh,700px)] xl:flex-1 xl:min-h-0 xl:max-h-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner text="Loading stores..." />
            </div>
          ) : isError ? (
            <div className="glass-card p-8 text-center">
              <AlertTriangle size={40} className="text-amber-400 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">Failed to load store directory</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors text-sm"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <AnimatePresence mode="popLayout">
                  {(data?.stores ?? []).map((store, i) => (
                    <motion.div
                      key={store.store_id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: Math.min(i * 0.015, 0.4) }}
                    >
                      <StoreCard
                        store={store}
                        onClick={() => {
                          trackEvent('Store Drilldown', { store_id: store.store_id, source: 'store_list' })
                          navigate(`/stores/${store.store_id}`)
                        }}
                        showVar={sortBy === 'value_at_risk'}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {(data?.stores ?? []).length === 0 && (
                <div className="text-center py-16 text-slate-500">
                  <Store size={48} className="mx-auto mb-3 opacity-30" />
                  <p>No stores found matching your search</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 max-w-7xl w-full mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between py-3 border-t border-white/5">
            <span className="text-xs text-slate-500">
              Page {page} of {totalPages} • {data?.total.toLocaleString()} stores
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={14} />
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      p === page
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                        : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {p}
                  </button>
                )
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
